import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Alert,
  Divider,
} from "@mui/material";
import {
  ArrowForward as ArrowForwardIcon,
  Favorite as HeartIcon,
} from "@mui/icons-material";
import { isEmail } from "../utils/helper";
import PageLayout from "../components/Common/PageLayout";
import PageBanner from "../components/Common/PageBanner";

const pageInfo = {
  slug: "/donate",
  title: "Donate",
};

/**
 *
 * @returns {JSX.Element}
 * @component
 */
const Donate = () => {
  const isLoading = useSelector((state) => state.pageContents?.loading);
  const user = useSelector((state) => state.user);

  const donateFormRef = useRef(null);

  // Donation state
  const [donorEmail, setDonorEmail] = useState("");

  // Form state
  const [formErrors, setFormErrors] = useState({});
  // eslint-disable-next-line no-unused-vars
  const [formErrorMsg, setFormErrorMsg] = useState("");

  const {
    about: { about: aboutData },
  } = useSelector((state) => state.pageContents) || {};

  // Pre-populate form data
  useEffect(() => {
    // Pre-populate email from user data if logged in
    if (user?.data?.email) {
      setDonorEmail(user.data.email);
    }
  }, [user]);

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    // Validate email
    if (!donorEmail.trim()) {
      errors.email = "Email is required";
      isValid = false;
    } else if (!isEmail(donorEmail)) {
      errors.email = "Invalid email address";
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleDonate = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      donateFormRef.current.scrollIntoView({ behavior: "smooth" });
      return;
    }

    // Redirect to Stripe payment link with email parameter
    const donateUrl = new URL(process.env.REACT_APP_STRIPE_DONATE_URL);
    donateUrl.searchParams.append("prefilled_email", donorEmail);
    window.open(donateUrl.toString(), "_blank");
  };

  return (
    <PageLayout
      pageName={pageInfo.title}
      showLoader={isLoading}
      checkComingSoon
    >
      {/* Banner */}
      {aboutData?.donate ? (
        <PageBanner
          dense
          bannerImage={aboutData.donate.banner_image}
          mainTitle={aboutData.donate.banner_title || pageInfo.title}
          pageTitle={pageInfo.title}
        />
      ) : (
        <PageBanner
          dense
          mainTitle={pageInfo.title}
          pageTitle={pageInfo.title}
        />
      )}

      {/* Donation Area */}
      <Box component="section" sx={{ py: 8 }}>
        <Container maxWidth="md" ref={donateFormRef}>
          {formErrorMsg && (
            <Alert severity="error" sx={{ mb: 4 }}>
              {formErrorMsg}
            </Alert>
          )}

          <Paper elevation={3} sx={{ p: 4, mb: 4, borderRadius: 2 }}>
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <HeartIcon color="error" sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h4" gutterBottom>
                Support Our Mission
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Your contribution helps us advance research and education in
                medicine and healthcare. All donations are tax-deductible and
                will be used to fund important initiatives.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <form onSubmit={handleDonate}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    value={donorEmail}
                    onChange={(e) => setDonorEmail(e.target.value)}
                    error={!!formErrors.email}
                    helperText={formErrors.email}
                    required
                    type="email"
                    disabled={user?.data?.email}
                  />
                </Grid>

                <Grid size={{ xs: 12 }}>
                  <Box sx={{ mt: 3, textAlign: "center" }}>
                    <Button
                      variant="contained"
                      size="large"
                      color="primary"
                      type="submit"
                      endIcon={<ArrowForwardIcon />}
                      sx={{ px: 4, py: 1.5 }}
                    >
                      Proceed to Donate
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>

          <Typography variant="body2" color="text.secondary" textAlign="center">
            Thank you for your generosity! For questions about donations or to
            donate by check, please contact us at smfahq@gmail.com.
          </Typography>
        </Container>
      </Box>
    </PageLayout>
  );
};

export default Donate;
